import React, { SyntheticEvent, useCallback } from "react";
import { connect } from "react-redux";

import Announcement from "@mapmycustomers/shared/types/announcements/Announcement";
import { Modal } from "@mapmycustomers/ui";

import {
  hideAnnouncements,
  postponeAnnouncement,
  showNextAnnouncement,
} from "@app/store/announcements/actions";
import {
  getAnnouncementQueue,
  getAnnouncementsModalPageId,
  getAnnouncementsModalTrigger,
  getCurrentAnnouncementIndex,
  isAnnouncementsModalOpen,
} from "@app/store/announcements/selectors";
import { RootState } from "@app/store/rootReducer";
import getAnnouncementCta from "@app/util/announcements/getAnnouncementCta";
import getAnnouncementsCloseSource from "@app/util/announcements/getAnnouncementsCloseSource";
import { ANNOUNCEMENTS_MODAL_ZINDEX } from "@app/util/consts";

import AnnouncementCard from "./AnnouncementCard";
import AnnouncementCtaButton from "./AnnouncementCtaButton";
import AnnouncementFooter from "./AnnouncementFooter";
import styles from "./Announcements.module.scss";
import useAnnouncementsAnalytics from "./useAnnouncementsAnalytics";

const Announcements = ({
  currentIndex,
  onHide,
  onPostpone,
  onShowNext,
  open,
  pageId,
  queue,
  trigger,
}: ReturnType<typeof mapStateToProps> & typeof mapDispatchToProps) => {
  const current = queue[currentIndex];
  const { trackClose, trackCtaClick, trackNext, trackRemindLater } = useAnnouncementsAnalytics({
    currentIndex,
    open,
    pageId,
    queue,
    trigger,
  });

  const handleNext = useCallback(
    (announcement: Announcement) => {
      trackNext(announcement);
      onShowNext(announcement.id);
    },
    [onShowNext, trackNext]
  );
  const handleRemindLater = useCallback(
    (announcement: Announcement) => {
      trackRemindLater(announcement);
      onPostpone(announcement.id);
    },
    [onPostpone, trackRemindLater]
  );
  const handleClose = useCallback(
    (event: SyntheticEvent) => {
      trackClose(getAnnouncementsCloseSource(event));
      onHide();
    },
    [onHide, trackClose]
  );
  // Both CTA kinds open a new tab, so the modal stays on this card and nothing is marked viewed.
  const handleCtaClick = useCallback(
    (announcement: Announcement) => {
      const cta = getAnnouncementCta(announcement);
      if (cta) {
        trackCtaClick(announcement, cta);
      }
    },
    [trackCtaClick]
  );

  if (!open || !current) {
    return null;
  }

  // X and Esc reach onCancel and hide announcements for the session; backdrop clicks do nothing.
  return (
    <Modal
      className={styles.modal}
      footer={
        <div className={styles.footer}>
          <AnnouncementCtaButton announcement={current} onClick={handleCtaClick} />
          <AnnouncementFooter
            announcement={current}
            currentIndex={currentIndex}
            onNext={handleNext}
            onRemindLater={handleRemindLater}
            total={queue.length}
          />
        </div>
      }
      maskClosable={false}
      onCancel={handleClose}
      open
      scrollable
      title={null}
      width="36rem"
      zIndex={ANNOUNCEMENTS_MODAL_ZINDEX}
    >
      <AnnouncementCard announcement={current} />
    </Modal>
  );
};

const mapStateToProps = (state: RootState) => ({
  currentIndex: getCurrentAnnouncementIndex(state),
  open: isAnnouncementsModalOpen(state),
  pageId: getAnnouncementsModalPageId(state),
  queue: getAnnouncementQueue(state),
  trigger: getAnnouncementsModalTrigger(state),
});

const mapDispatchToProps = {
  onHide: hideAnnouncements,
  onPostpone: postponeAnnouncement,
  onShowNext: showNextAnnouncement,
};

export default connect(mapStateToProps, mapDispatchToProps)(Announcements);
